// MainPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../../context/AuthContext';
import { employeeCoursesSummaryService } from '../../services/employeeCoursesSummaryService';
import { statisticsService } from '../../services/statisticsService';
import { courseEventService } from '../../services/courseEventService';
import { employeeService } from '../../services/employeeService';
import { courseService } from '../../services/courseService';
import { trainingEventService } from '../../services/trainingEventService';
import { apiClient, API_ENDPOINTS } from '../../services/api';
import { useAppDataRefresh } from '../../hooks/useAppDataRefresh';
import { useNotifyDataChange } from '../../hooks/useNotifyDataChange';
import LoadingState from '../HeatMap/LoadingState';
import ErrorState from '../HeatMap/ErrorState';
import EnrollToEventModal from './EnrollToEventModal';
import ConfirmEventModal from './ConfirmEventModal';
import MainPageSkeleton from './MainPageSkeleton';
import CourseEventInfo from './CourseEventInfo';
import CompleteTrainingModal from './CompleteTrainingModal';
import './MainPage.css';

// SVG иконки
const CalendarIcon = ({ width = 14, height = 14, strokeWidth = 2 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const CheckCircleIcon = ({ width = 14, height = 14 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const NotRequiredIcon = ({ width = 14, height = 14 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronDownIcon = ({ isExpanded }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="20" height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={`accordion-chevron ${isExpanded ? 'rotated' : ''}`}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const EmptyStateIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-gray-300 mb-3">
    <path d="M20 12v8H4v-8M12 2v12m0 0-3-3m3 3 3-3" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const UsersIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const XIcon = ({ width = 14, height = 14 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Типы вкладок для блока мероприятий
const EVENTS_TAB = {
  UPCOMING: 'upcoming',
  PENDING_CONFIRMATION: 'pending_confirmation'
};

// Цветовая схема для категорий блоков
const CATEGORY_COLORS = {
  'Медицина': { bg: '#fef2f2', border: 'rgb(182, 182, 182)', text: '#991b1b', light: '#ffffff', icon: '' },
  'Охрана труда': { bg: '#fefce8', border: 'rgb(182, 182, 182)', text: '#854d0e', light: '#fef9c3', icon: '' },
  'Пожарная безопасность': { bg: '#fff7ed', border: 'rgb(182, 182, 182)', text: '#9a3412', light: '#ffedd5', icon: '' },
  'Электробезопасность': { bg: '#eff6ff', border: 'rgb(182, 182, 182)', text: '#1e3a8a', light: '#dbeafe', icon: '' },
  'Промышленная безопасность': { bg: '#f0fdf4', border: 'rgb(182, 182, 182)', text: '#166534', light: '#dcfce7', icon: '' },
  'Информационная безопасность': { bg: '#f5f3ff', border: 'rgb(182, 182, 182)', text: '#4c1d95', light: '#ede9fe', icon: '' },
  'Экологическая безопасность': { bg: '#ecfdf5', border: 'rgb(182, 182, 182)', text: '#065f46', light: '#d1fae5', icon: '' },
  'Гражданская оборона': { bg: '#fef2f2', border: 'rgb(182, 182, 182)', text: '#991b1b', light: '#fee2e2', icon: '' },
  'default': { bg: '#f8fafc', border: '#64748b', text: '#334155', light: '#ffffff', icon: '' }
};

// Функция получения цвета для категории
const getCategoryColor = (categoryName) => {
  if (!categoryName) return CATEGORY_COLORS.default;
  const found = Object.keys(CATEGORY_COLORS).find(
    key => categoryName.toLowerCase().includes(key.toLowerCase())
  );
  return CATEGORY_COLORS[found] || CATEGORY_COLORS.default;
};

const MainPage = () => {
  const { userRole, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [groupBy, setGroupBy] = useState('byCourse');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [blockFilter, setBlockFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('');
  
  // Состояния для группировки по категориям
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const [loading, setLoading] = useState(true);
  
  const [eventsTab, setEventsTab] = useState(EVENTS_TAB.UPCOMING);
  
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  const [loadingState, setLoadingState] = useState({
    summary: true,
    statistics: true,
    departments: true,
    categories: true,
    attentionItems: false,
    upcomingEvents: true,
    pendingEvents: true
  });
  
  const [error, setError] = useState(null);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [summaryData, setSummaryData] = useState({
    expired: { total: 0, byCourse: [], employeesList: [] },
    expiringInMonth: { total: 0, byCourse: [], employeesList: [] },
    expiringInTwoMonths: { total: 0, byCourse: [], employeesList: [] },
    required: { total: 0, byCourse: [], employeesList: [] },
    statistics: {
      totalTrainings: 0,
      expiredTotal: 0,
      expiringMonthTotal: 0,
      expiringTwoMonthsTotal: 0,
      requiredTotal: 0,
      activeTotal: 0,
      uniqueEmployeesWithExpired: 0,
      uniqueEmployeesExpiringMonth: 0,
      uniqueEmployeesExpiringTwoMonths: 0,
      uniqueEmployeesRequired: 0,
      registeredForEvents: { expired: 0, expiring_month: 0, expiring_two_months: 0, required: 0 }
    }
  });
  const [employeeStatistics, setEmployeeStatistics] = useState({
    total: 0,
    compliant: 0,
    nonCompliant: 0,
    compliancePercentage: 0,
    byPosition: {}
  });
  const [attentionItems, setAttentionItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [cardStats, setCardStats] = useState({
    expired: { byCourse: [], total: 0, totalRegistered: 0, totalNotRegistered: 0 },
    expiringInMonth: { byCourse: [], total: 0, totalRegistered: 0, totalNotRegistered: 0 },
    expiringInTwoMonths: { byCourse: [], total: 0, totalRegistered: 0, totalNotRegistered: 0 },
    required: { byCourse: [], total: 0, totalRegistered: 0, totalNotRegistered: 0 }
  });
  
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingEventsLoading, setUpcomingEventsLoading] = useState(true);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [pendingEventsLoading, setPendingEventsLoading] = useState(true);
  
  // Состояния для модального окна подтверждения мероприятия
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedEventForConfirm, setSelectedEventForConfirm] = useState(null);
  
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [selectedCourseForEnroll, setSelectedCourseForEnroll] = useState(null);
  const [selectedEmployeeForEnroll, setSelectedEmployeeForEnroll] = useState(null);
  const [bulkEnrollData, setBulkEnrollData] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState({});
  const [bulkEnrolling, setBulkEnrolling] = useState({});
  const [completingTrainings, setCompletingTrainings] = useState({});
  
  // Состояния для модального окна отметки обучения как пройденного
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedItemForComplete, setSelectedItemForComplete] = useState(null);
  
  const isInitialMount = useRef(true);
  const isLoadingData = useRef(false);
  const lastLoadTime = useRef(0);
  const pendingRefresh = useRef(null);

  const { notifyTrainingCompleted, notifyCacheInvalidated } = useNotifyDataChange();

  // Сохранение состояния аккордиона в localStorage
  useEffect(() => {
    if (Object.keys(expandedGroups).length > 0) {
      localStorage.setItem('mainPage_expandedGroups', JSON.stringify(expandedGroups));
    } else {
      localStorage.removeItem('mainPage_expandedGroups');
    }
  }, [expandedGroups]);

  // Сохранение состояния категорий
  useEffect(() => {
    if (Object.keys(expandedCategories).length > 0) {
      localStorage.setItem('mainPage_expandedCategories', JSON.stringify(expandedCategories));
    } else {
      localStorage.removeItem('mainPage_expandedCategories');
    }
  }, [expandedCategories]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  }, []);

  // Загрузка ближайших мероприятий - ИСПРАВЛЕНО: включаем сегодняшний день
  const loadUpcomingEvents = useCallback(async (forceRefresh = false) => {
    try {
      setUpcomingEventsLoading(true);
      
      const allEvents = await trainingEventService.getAll({}, forceRefresh);
      
      // Нормализуем сегодняшнюю дату к началу дня (00:00:00)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Вычисляем дату через 30 дней (конец дня)
      const nextMonth = new Date(today);
      nextMonth.setDate(today.getDate() + 30);
      nextMonth.setHours(23, 59, 59, 999);
      
      const filtered = allEvents
        .filter(event => {
          if (!event.start_date) return false;
          const eventDate = new Date(event.start_date);
          // Нормализуем дату мероприятия к началу дня
          eventDate.setHours(0, 0, 0, 0);
          // Теперь сравнение включает сегодняшний день
          return eventDate >= today && eventDate <= nextMonth;
        })
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
        .slice(0, 5);
      
      setUpcomingEvents(filtered);
    } catch (err) {
      setUpcomingEvents([]);
    } finally {
      setUpcomingEventsLoading(false);
      setLoadingState(prev => ({ ...prev, upcomingEvents: false }));
    }
  }, []);

  // Загрузка мероприятий, ожидающих подтверждения
  const loadPendingEvents = useCallback(async (forceRefresh = false) => {
    try {
      setPendingEventsLoading(true);
      
      const allEvents = await trainingEventService.getAll({}, forceRefresh);
      
      const filtered = allEvents
        .filter(event => event.status === 'awaiting_confirmation')
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      
      setPendingEvents(filtered);
    } catch (err) {
      setPendingEvents([]);
    } finally {
      setPendingEventsLoading(false);
      setLoadingState(prev => ({ ...prev, pendingEvents: false }));
    }
  }, []);

  // Открытие модального окна подтверждения мероприятия
  const handleOpenConfirmModal = (event) => {
    setSelectedEventForConfirm(event);
    setConfirmModalOpen(true);
  };

  // Закрытие модального окна
  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedEventForConfirm(null);
  };

  // Обработчик успешного подтверждения мероприятия
  const handleConfirmSuccess = useCallback(async () => {
    await Promise.all([
      loadUpcomingEvents(true),
      loadPendingEvents(true),
      trainingEventService.invalidateCache(),
      employeeCoursesSummaryService.invalidateCache()
    ]);
  }, [loadUpcomingEvents, loadPendingEvents]);

  // Загрузка всех данных
  const loadAllData = useCallback(async (forceRefresh = false, preserveExpandedState = true) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('Требуется авторизация');
      setLoading(false);
      return;
    }
    
    const now = Date.now();
    if (!forceRefresh && now - lastLoadTime.current < 30000) {
      return;
    }
    
    if (isLoadingData.current) {
      return;
    }
    
    isLoadingData.current = true;
    
    const savedExpandedState = preserveExpandedState ? { ...expandedGroups } : null;
    const savedCategoriesState = preserveExpandedState ? { ...expandedCategories } : null;
    
    try {
      if (!forceRefresh && isInitialMount.current) {
        setLoading(true);
      }
      
      setError(null);
      
      if (forceRefresh) {
        employeeCoursesSummaryService.invalidateCache();
      }
      
      const summary = await employeeCoursesSummaryService.getSummary(forceRefresh);
      setSummaryData(summary);
      setLoadingState(prev => ({ ...prev, summary: false }));
      
      const items = employeeCoursesSummaryService.getAttentionItems(summary);
      setAttentionItems(items);
      setLoadingState(prev => ({ ...prev, attentionItems: false }));
      
      const [statsData, departments, categories] = await Promise.all([
        statisticsService.getEmployeesStatistics(forceRefresh),
        employeeService.getAllDepartments(),
        courseService.getCategories(forceRefresh)
      ]);
      
      let departmentsArray = [];
      if (Array.isArray(departments)) departmentsArray = departments;
      else if (departments?.data && Array.isArray(departments.data)) departmentsArray = departments.data;
      else if (departments?.departments && Array.isArray(departments.departments)) departmentsArray = departments.departments;
      
      setDepartmentsList([
        { id: 'all', name: 'Все подразделения' },
        ...departmentsArray.map(dept => ({ id: String(dept.id), name: dept.name }))
      ]);
      setLoadingState(prev => ({ ...prev, departments: false }));
      
      let categoriesArray = [];
      if (Array.isArray(categories)) categoriesArray = categories;
      else if (categories?.data && Array.isArray(categories.data)) categoriesArray = categories.data;
      else if (categories?.categories_with_details && Array.isArray(categories.categories_with_details)) categoriesArray = categories.categories_with_details;
      
      const formattedCategories = [
        { id: 'all', name: 'Все блоки', order: -1 },
        ...categoriesArray.map((cat, index) => ({
          id: String(cat.id || (typeof cat === 'string' ? cat : `cat_${Math.random()}`)),
          name: cat.name || cat || 'Без названия',
          order: index
        }))
      ];
      setCategoriesList(formattedCategories);
      setLoadingState(prev => ({ ...prev, categories: false }));
      
      setEmployeeStatistics(statsData);
      setLoadingState(prev => ({ ...prev, statistics: false }));
      
      if (forceRefresh || now - lastLoadTime.current > 15000) {
        await Promise.all([
          loadUpcomingEvents(forceRefresh),
          loadPendingEvents(forceRefresh)
        ]);
      }
      
      lastLoadTime.current = now;
      
      // Восстанавливаем состояния
      if (savedExpandedState && Object.keys(savedExpandedState).length > 0) {
        const currentGroupIds = getCurrentGroupIdsForRestore(items);
        const restoredState = {};
        Object.keys(savedExpandedState).forEach(groupId => {
          if (currentGroupIds.has(groupId)) {
            restoredState[groupId] = true;
          }
        });
        setExpandedGroups(restoredState);
      }
      
      if (savedCategoriesState && Object.keys(savedCategoriesState).length > 0) {
        setExpandedCategories(savedCategoriesState);
      }
      
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(err.message || 'Не удалось загрузить данные');
      }
    } finally {
      setLoading(false);
      isLoadingData.current = false;
    }
  }, [loadUpcomingEvents, loadPendingEvents, expandedGroups, expandedCategories]);

  const getCurrentGroupIdsForRestore = useCallback((items) => {
    const ids = new Set();
    
    if (groupBy === 'byCourse') {
      items.forEach(item => {
        const key = `${item.courseId}|${item.courseName}`;
        ids.add(key);
      });
    } else if (groupBy === 'byEmployee') {
      items.forEach(item => {
        const key = `${item.employeeId}|${item.employeeName}`;
        ids.add(key);
      });
    }
    
    return ids;
  }, [groupBy]);

  // Обработчик глобальных изменений данных
  const handleGlobalDataRefresh = useCallback(async (eventData) => {
    if (pendingRefresh.current) {
      clearTimeout(pendingRefresh.current);
    }
    
    pendingRefresh.current = setTimeout(() => {
      if (eventData?.type === 'training_completed') {
        employeeCoursesSummaryService.invalidateCache();
        employeeCoursesSummaryService.getSummary(true).then(summary => {
          setSummaryData(summary);
          const items = employeeCoursesSummaryService.getAttentionItems(summary);
          setAttentionItems(items);
        }).catch(console.error);
      } else if (eventData?.type === 'enrollment_created' || eventData?.type === 'enrollment_cancelled') {
        loadUpcomingEvents(true);
        loadPendingEvents(true);
      } else {
        loadAllData(true, true);
      }
      pendingRefresh.current = null;
    }, 1000);
  }, [loadAllData, loadUpcomingEvents, loadPendingEvents]);

  useAppDataRefresh(handleGlobalDataRefresh);

  useEffect(() => {
    if (user && !authLoading && isInitialMount.current) {
      isInitialMount.current = false;
      loadAllData(false, false);
    }
  }, [user, authLoading, loadAllData]);

  // Фильтрация
  useEffect(() => {
    let filtered = [...attentionItems];
    
    if (departmentFilter && departmentFilter !== 'all') {
      filtered = filtered.filter(item => String(item.departmentId) === String(departmentFilter));
    }
    
    if (blockFilter && blockFilter !== 'all' && categoriesList.length > 0) {
      const selectedBlock = categoriesList.find(c => String(c.id) === String(blockFilter));
      if (selectedBlock?.name && selectedBlock.name !== 'all') {
        filtered = filtered.filter(item => item.courseCategory === selectedBlock.name);
      }
    }
    
    if (levelFilter && levelFilter !== '') {
      filtered = filtered.filter(item => item.level === levelFilter);
    }
    
    setFilteredItems(filtered);
  }, [departmentFilter, blockFilter, levelFilter, attentionItems, categoriesList]);

  // Обновление статистики карточек
  useEffect(() => {
    if (attentionItems.length > 0 || !loadingState.attentionItems) {
      const stats = employeeCoursesSummaryService.getCardStatistics(summaryData, attentionItems);
      setCardStats(stats);
    }
  }, [attentionItems, summaryData, loadingState.attentionItems]);

  // Открытие модального окна для отметки обучения как пройденного
  const handleOpenCompleteModal = (item) => {
    setSelectedItemForComplete(item);
    setCompleteModalOpen(true);
  };

  const handleCloseCompleteModal = () => {
    setCompleteModalOpen(false);
    setSelectedItemForComplete(null);
  };

  // Функция отметки обучения как пройденного
  const handleCompleteTraining = async (item, completedDate) => {
    if (completingTrainings[item.id]) return;
    
    setCompletingTrainings(prev => ({ ...prev, [item.id]: true }));
    
    try {
      const trainingId = item.trainingId;
      
      if (!trainingId) {
        showToast('Ошибка: не удалось определить ID обучения', 'error');
        return;
      }
      
      await apiClient.put(
        API_ENDPOINTS.TRAINING_CERTIFICATE(trainingId),
        { certificate_number: null, completed_date: completedDate }
      );
      
      employeeCoursesSummaryService.invalidateCache();
      
      notifyTrainingCompleted({
        trainingId: trainingId,
        employeeId: item.employeeId,
        courseId: item.courseId,
        courseName: item.courseName,
        employeeName: item.employeeName,
        completedDate: completedDate
      });
      
      await loadAllData(true, true);
      showToast(`Обучение "${item.courseName}" отмечено как пройденное для сотрудника ${item.employeeName}`, 'success');
    } catch (err) {
      showToast(`Не удалось отметить обучение: ${err.message || 'Ошибка сервера'}`, 'error');
    } finally {
      setCompletingTrainings(prev => ({ ...prev, [item.id]: false }));
    }
  };

  // Обработчик успешной записи на мероприятие
  const handleEnrollSuccess = useCallback((event, employees) => {
    employeeCoursesSummaryService.invalidateCache();
    if (event?.course_id) {
      courseEventService.invalidateCache(event.course_id);
    }
    trainingEventService.invalidateCache();
    
    if (pendingRefresh.current) {
      clearTimeout(pendingRefresh.current);
    }
    pendingRefresh.current = setTimeout(() => {
      loadAllData(true, true);
      pendingRefresh.current = null;
    }, 500);
  }, [loadAllData]);

  // Вспомогательные функции для получения статусов
 const getEnrollmentStatusForItem = (item) => {
  if (item.registeredForEvent && item.eventInfo) {
    const eventDate = new Date(item.eventInfo.event_start_date);
    const now = new Date();
    // Нормализуем обе даты к началу дня
    const eventDateOnly = new Date(eventDate);
    eventDateOnly.setHours(0, 0, 0, 0);
    const nowOnly = new Date(now);
    nowOnly.setHours(0, 0, 0, 0);
    // Считаем предстоящим, если дата мероприятия >= сегодня
    const isUpcoming = eventDateOnly >= nowOnly;
    return {
      isEnrolled: true,
      eventId: item.eventInfo.event_id,
      eventTitle: item.eventInfo.event_title,
      eventStartDate: item.eventInfo.event_start_date,
      participantStatus: item.eventInfo.participant_status,
      isUpcoming: isUpcoming
    };
  }
  return { isEnrolled: false };
};

 const getEnrollButtonText = (item) => {
  const status = getEnrollmentStatusForItem(item);
  if (status.isEnrolled) {
    // Всегда показываем "Записан", даже если мероприятие уже началось
    return 'Записан';
  }
  return 'Записать';
};

  const isEnrollButtonDisabled = (item) => {
    const status = getEnrollmentStatusForItem(item);
    return status.isEnrolled || checkingAvailability[item.id];
  };

  const getEnrollButtonTitle = (item) => {
    const status = getEnrollmentStatusForItem(item);
    if (status.isEnrolled) {
      if (status.eventTitle && status.eventStartDate) {
        const startDate = new Date(status.eventStartDate).toLocaleDateString('ru-RU');
        const statusText = status.participantStatus === 'registered' ? 'Зарегистрирован' : status.participantStatus;
        return `${statusText} на "${status.eventTitle}" (${startDate})`;
      }
      return 'Уже записан на обучение';
    }
    return 'Записать на обучение';
  };

  const handleEnrollClick = async (item) => {
    setSelectedCourseForEnroll({
      id: item.courseId,
      name: item.courseName
    });
    setSelectedEmployeeForEnroll({
      employeeId: item.employeeId,
      employeeName: item.employeeName,
      department: item.department,
      personnelNumber: item.personnelNumber,
      position: item.position
    });
    
    setCheckingAvailability(prev => ({ ...prev, [item.id]: true }));
    
    try {
      const hasEvents = await courseEventService.hasAvailableEvents(item.courseId);
      if (!hasEvents) {
        showToast(`Нет доступных мероприятий для курса "${item.courseName}". Пожалуйста, обратитесь к администратору.`, 'error');
        setCheckingAvailability(prev => ({ ...prev, [item.id]: false }));
        return;
      }
      setEnrollModalOpen(true);
    } catch (error) {
      showToast('Не удалось проверить доступность мероприятий. Попробуйте позже.', 'error');
    } finally {
      setCheckingAvailability(prev => ({ ...prev, [item.id]: false }));
    }
  };

  // Функция для массовой записи всех сотрудников курса
  const handleBulkEnrollClick = useCallback(async (group) => {
    const notEnrolledEmployees = group.items
      .filter(item => {
        const status = getEnrollmentStatusForItem(item);
        return !status.isEnrolled;
      })
      .map(item => ({
        employeeId: item.employeeId,
        employeeName: item.employeeName,
        department: item.department,
        position: item.position,
        personnelNumber: item.personnelNumber
      }));
    
    if (notEnrolledEmployees.length === 0) {
      showToast('Все сотрудники уже записаны на обучение', 'info');
      return;
    }
    
    try {
      const hasEvents = await courseEventService.hasAvailableEvents(group.courseId);
      if (!hasEvents) {
        showToast(`Нет доступных мероприятий для курса "${group.courseName}". Пожалуйста, обратитесь к администратору.`, 'error');
        return;
      }
      
      setBulkEnrollData({
        courseId: group.courseId,
        courseName: group.courseName,
        employees: notEnrolledEmployees
      });
      setEnrollModalOpen(true);
    } catch (error) {
      showToast('Не удалось проверить доступность мероприятий. Попробуйте позже.', 'error');
    }
  }, [showToast]);

  // Вспомогательные функции для отображения
  const getLevelLabel = (level) => {
    switch(level) {
      case 'expired': return 'Просрочено';
      case 'expiring_month': return 'Ближайший месяц';
      case 'expiring_two_months': return 'Через 1-2 месяца';
      case 'required': return 'Требуется пройти';
      default: return '';
    }
  };

  const getStatusText = (item) => {
    if (item.status === 'required') {
      return 'Требуется пройти';
    }
    if (item.status === 'overdue') {
      const days = Math.round(item.daysOverdue);
      if (days === 1) return 'Просрочено 1 день';
      if (days < 5) return `Просрочено ${days} дня`;
      return `Просрочено ${days} дн.`;
    }
    const days = Math.round(item.daysLeft);
    if (days <= 0) return 'Истекает сегодня';
    if (days === 1) return 'Истекает завтра';
    if (days < 5) return `Истекает через ${days} дня`;
    return `Истекает через ${days} дн.`;
  };

  const getBorderColor = (item) => {
    if (item.status === 'required') return 'bg-blue-500';
    if (item.status === 'overdue') return 'bg-red-500';
    if (item.level === 'critical') return 'bg-red-500';
    if (item.level === 'urgent') return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const getBadgeColor = (level) => {
    switch(level) {
      case 'critical': return 'badge-critical';
      case 'urgent': return 'badge-urgent';
      case 'planning': return 'badge-planning';
      case 'required': return 'badge-required';
      default: return '';
    }
  };

  const getStatusColor = (item) => {
    if (item.status === 'required') return 'text-blue-600';
    if (item.status === 'overdue') return 'text-red-600';
    if (item.daysLeft <= 7) return 'text-amber-600';
    return 'text-gray-500';
  };

  const getCardBorderColor = (type) => {
    switch(type) {
      case 'expired': return 'border-red-500';
      case 'expiring': return 'border-amber-500';
      case 'planning': return 'border-yellow-400';
      case 'required': return 'border-blue-500';
      default: return 'border-gray-500';
    }
  };

  const getCounterColor = (type) => {
    switch(type) {
      case 'expired': return 'text-red-600';
      case 'expiring': return 'text-amber-600';
      case 'planning': return 'text-yellow-600';
      case 'required': return 'text-blue-600';
      default: return 'text-gray-900';
    }
  };

  const getIconColor = (type) => {
    switch(type) {
      case 'expired': return 'text-red-500';
      case 'expiring': return 'text-amber-500';
      case 'planning': return 'text-yellow-500';
      case 'required': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  // Функция для сортировки категорий в соответствии с порядком в фильтре "Блоки"
  const sortCategoriesByFilterOrder = useCallback((categories) => {
    // Создаем карту порядка категорий из фильтра
    const categoryOrderMap = new Map();
    categoriesList.forEach((cat, index) => {
      if (cat.name !== 'Все блоки') {
        categoryOrderMap.set(cat.name, index);
      }
    });
    
    // Сортируем категории
    return [...categories].sort((a, b) => {
      const orderA = categoryOrderMap.get(a.categoryName) ?? 999;
      const orderB = categoryOrderMap.get(b.categoryName) ?? 999;
      return orderA - orderB;
    });
  }, [categoriesList]);

  // Функция группировки курсов по категориям
  const groupCoursesByCategory = useCallback((courses) => {
    const grouped = new Map();
    
    courses.forEach(course => {
      const category = course.courseCategory || 'Без категории';
      if (!grouped.has(category)) {
        grouped.set(category, {
          categoryName: category,
          categoryColor: getCategoryColor(category),
          courses: [],
          totalEmployees: 0,
          criticalCount: 0,
          urgentCount: 0,
          planningCount: 0,
          requiredCount: 0
        });
      }
      const group = grouped.get(category);
      group.courses.push(course);
      group.totalEmployees += course.totalEmployees;
      group.criticalCount += course.criticalCount;
      group.urgentCount += course.urgentCount;
      group.planningCount += course.planningCount;
      group.requiredCount += course.requiredCount;
    });
    
    // Сортируем категории в соответствии с порядком из фильтра
    const groupedArray = Array.from(grouped.values());
    return sortCategoriesByFilterOrder(groupedArray);
  }, [sortCategoriesByFilterOrder]);

  // Группировка элементов
  const getGroupedItems = useCallback(() => {
    if (groupBy === 'list') {
      return { type: 'list', items: filteredItems };
    }
    
    if (groupBy === 'byCourse') {
      // Сначала группируем по курсам
      const coursesMap = new Map();
      filteredItems.forEach(item => {
        const key = `${item.courseId}|${item.courseName}`;
        if (!coursesMap.has(key)) {
          coursesMap.set(key, {
            id: key,
            courseId: item.courseId,
            courseName: item.courseName,
            courseCategory: item.courseCategory || 'Без категории',
            items: [],
            totalEmployees: 0,
            criticalCount: 0,
            urgentCount: 0,
            planningCount: 0,
            requiredCount: 0,
            minDaysLeft: Infinity,
            maxDaysOverdue: 0
          });
        }
        const course = coursesMap.get(key);
        course.items.push(item);
        course.totalEmployees++;
        if (item.level === 'critical') course.criticalCount++;
        if (item.level === 'urgent') course.urgentCount++;
        if (item.level === 'planning') course.planningCount++;
        if (item.level === 'required') course.requiredCount++;
        if (item.daysLeft > 0 && item.daysLeft < course.minDaysLeft) course.minDaysLeft = item.daysLeft;
        if (item.daysOverdue > course.maxDaysOverdue) course.maxDaysOverdue = item.daysOverdue;
      });
      
      // Группируем курсы по категориям с сортировкой по порядку из фильтра
      const coursesArray = Array.from(coursesMap.values());
      const groupedByCategory = groupCoursesByCategory(coursesArray);
      
      return { type: 'byCourseWithCategories', categories: groupedByCategory };
    }
    
    if (groupBy === 'byEmployee') {
      const grouped = new Map();
      filteredItems.forEach(item => {
        const key = `${item.employeeId}|${item.employeeName}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            id: key,
            employeeId: item.employeeId,
            employeeName: item.employeeName,
            department: item.department,
            personnelNumber: item.personnelNumber,
            position: item.position,
            items: [],
            totalCourses: 0,
            criticalCount: 0,
            urgentCount: 0,
            planningCount: 0,
            requiredCount: 0
          });
        }
        const group = grouped.get(key);
        group.items.push(item);
        group.totalCourses++;
        if (item.level === 'critical') group.criticalCount++;
        if (item.level === 'urgent') group.urgentCount++;
        if (item.level === 'planning') group.planningCount++;
        if (item.level === 'required') group.requiredCount++;
      });
      return { type: 'byEmployee', groups: Array.from(grouped.values()) };
    }
    
    return { type: 'list', items: filteredItems };
  }, [filteredItems, groupBy, groupCoursesByCategory]);

  // Рендер элемента в режиме списка
  const renderListItem = (item) => {
    const enrollmentStatus = getEnrollmentStatusForItem(item);
    const isEnrolled = enrollmentStatus.isEnrolled;
    const buttonDisabled = isEnrollButtonDisabled(item);
    const buttonText = getEnrollButtonText(item);
    const buttonTitle = getEnrollButtonTitle(item);
    
    return (
      <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-200 border border-gray-100">
        <div className="flex">
          <div className={`w-1.5 ${getBorderColor(item)} flex-shrink-0`}></div>
          <div className="flex-1 p-4">
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <a className="employee-link" href={`/settings/employees/${item.employeeId}/trainings`}>
                    {item.employeeName}
                  </a>
                  <span className={`badge ${getBadgeColor(item.level)}`}>
                    {getLevelLabel(item.level)}
                  </span>
                  {item.personnelNumber && (
                    <span className="text-xs text-gray-400">Таб. № {item.personnelNumber}</span>
                  )}
                  {isEnrolled && enrollmentStatus.isUpcoming && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      ✓ Зарегистрирован
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-700 font-medium">{item.courseName}</div>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                  <span>{item.department}</span>
                  <span>·</span>
                  <span>{item.courseCategory}</span>
                  <span>·</span>
                  <span className={`font-semibold ${getStatusColor(item)}`}>
                    {getStatusText(item)}
                  </span>
                  {item.expirationDate && (
                    <span className="text-gray-400">
                      до {new Date(item.expirationDate).toLocaleDateString('ru-RU')}
                    </span>
                  )}
                </div>
              </div>
              <div className="action-buttons-top-right">
                <button 
                  className={`btn-action-main btn-record ${isEnrolled ? 'btn-enrolled' : ''}`}
                  title={buttonTitle}
                  onClick={() => !isEnrolled && handleEnrollClick(item)}
                  disabled={buttonDisabled}
                >
                  {checkingAvailability[item.id] ? (
                    <>
                      <div className="enroll-btn-spinner-small"></div>
                      <span>...</span>
                    </>
                  ) : (
                    <>
                      <CalendarIcon />
                      <span>{buttonText}</span>
                    </>
                  )}
                </button>
                <button 
                  className="btn-action-main btn-done" 
                  title="Пройдено"
                  onClick={() => handleOpenCompleteModal(item)}
                  disabled={completingTrainings[item.id]}
                >
                  {completingTrainings[item.id] ? (
                    <div className="enroll-btn-spinner-small"></div>
                  ) : (
                    <>
                      <CheckCircleIcon />
                      Пройдено
                    </>
                  )}
                </button>
                <button 
                  className="btn-action-icon-sm btn-danger" 
                  title="Не требуется"
                  onClick={() => showToast('Функционал в разработке', 'info')}
                >
                  <NotRequiredIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Рендер строки элемента в группе
  const renderGroupItemRow = (item, isEmployeeGroup = false) => {
    const enrollmentStatus = getEnrollmentStatusForItem(item);
    const isEnrolled = enrollmentStatus.isEnrolled;
    const buttonDisabled = isEnrollButtonDisabled(item);
    const buttonText = getEnrollButtonText(item);
    const buttonTitle = getEnrollButtonTitle(item);
    
    let borderColorClass = 'accordion-item-border-blue';
    if (item.status === 'required') {
      borderColorClass = 'accordion-item-border-blue';
    } else if (item.status === 'overdue' || item.level === 'critical') {
      borderColorClass = 'accordion-item-border-red';
    } else if (item.level === 'urgent') {
      borderColorClass = 'accordion-item-border-amber';
    }
    
    let levelBadgeClass = 'level-badge-planning';
    if (item.level === 'critical') {
      levelBadgeClass = 'level-badge-critical';
    } else if (item.level === 'urgent') {
      levelBadgeClass = 'level-badge-urgent';
    } else if (item.level === 'required') {
      levelBadgeClass = 'level-badge-required';
    }
    
    let statusColorClass = 'status-text-gray-accordion';
    if (item.status === 'required') {
      statusColorClass = 'status-text-blue-accordion';
    } else if (item.status === 'overdue') {
      statusColorClass = 'status-text-red-accordion';
    } else if (item.daysLeft <= 7) {
      statusColorClass = 'status-text-amber-accordion';
    }
    
    return (
      <div key={item.id} className="accordion-item-row">
        <div className={`accordion-item-border ${borderColorClass}`}></div>
        <div className="accordion-item-body">
          <div className="flex items-start justify-between gap-4">
            <div className="accordion-item-left">
              <div className="accordion-item-header">
                {isEmployeeGroup ? (
                  <span className="course-name-accordion">{item.courseName}</span>
                ) : (
                  <a 
                    className="employee-link-accordion" 
                    href={`/settings/employees/${item.employeeId}/trainings`}
                  >
                    {item.employeeName}
                  </a>
                )}
                <span className={`level-badge ${levelBadgeClass}`}>
                  {getLevelLabel(item.level)}
                </span>
                {isEnrolled && enrollmentStatus.isUpcoming && (
                  <span className="registered-badge-small">
                    Записан
                  </span>
                )}
              </div>
              <div className="accordion-item-meta">
                {isEmployeeGroup ? (
                  <span>{item.courseCategory}</span>
                ) : (
                  <>
                    <span>{item.department}</span>
                    {item.position && <><span>·</span><span>{item.position}</span></>}
                  </>
                )}
                <span>·</span>
                <span className={statusColorClass}>
                  {getStatusText(item)}
                </span>
                {item.expirationDate && (
                  <span className="text-gray-400">
                    до {new Date(item.expirationDate).toLocaleDateString('ru-RU')}
                  </span>
                )}
              </div>
            </div>
            <div className="accordion-item-actions">
              <button 
                className={`btn-record-small ${isEnrolled ? 'btn-enrolled' : ''}`}
                title={buttonTitle}
                onClick={() => !isEnrolled && handleEnrollClick(item)}
                disabled={buttonDisabled}
              >
                {checkingAvailability[item.id] ? (
                  <div className="enroll-spinner-small"></div>
                ) : (
                  <CalendarIcon width={12} height={12} />
                )}
                {buttonText}
              </button>
              <button 
                className="btn-done-small"
                onClick={() => handleOpenCompleteModal(item)}
                disabled={completingTrainings[item.id]}
              >
                {completingTrainings[item.id] ? (
                  <div className="enroll-spinner-small"></div>
                ) : (
                  <>
                    <CheckCircleIcon width={12} height={12} />
                    Пройдено
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Рендер группы по курсам (отдельный курс)
  const renderCourseGroup = (course) => {
    const isExpanded = expandedGroups[course.id] || false;
    const categoryColor = getCategoryColor(course.courseCategory);
    
    const notEnrolledCount = course.items.filter(item => {
      const status = getEnrollmentStatusForItem(item);
      return !status.isEnrolled;
    }).length;
    
    const allEnrolled = notEnrolledCount === 0;
    
    return (
      <div key={course.id} className="course-group-item" style={{ borderLeft: `3px solid ${categoryColor.border}` }}>
        <div 
          onClick={() => toggleGroup(course.id)}
          className="course-group-header"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleGroup(course.id);
            }
          }}
        >
          <ChevronDownIcon isExpanded={isExpanded} />
          <div className="course-group-header-left">
            <span className="course-group-title">{course.courseName}</span>
            <div className="course-group-event-info">
              <CourseEventInfo courseId={course.courseId} />
            </div>
          </div>
          <div className="course-group-header-right">
            {course.criticalCount > 0 && (
              <span className="badge-critical-count">{course.criticalCount} крит.</span>
            )}
            {course.urgentCount > 0 && (
              <span className="badge-urgent-count">{course.urgentCount} сроч.</span>
            )}
            {course.planningCount > 0 && (
              <span className="badge-planning-count">{course.planningCount} план.</span>
            )}
            {course.requiredCount > 0 && (
              <span className="badge-required-count">{course.requiredCount} треб.</span>
            )}
            <span className="total-count-badge">{course.totalEmployees}</span>
            <button 
              className="enroll-all-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleBulkEnrollClick(course);
              }}
              disabled={allEnrolled}
              title={allEnrolled ? 'Все сотрудники уже записаны' : `Записать ${notEnrolledCount} сотрудников`}
            >
              <CalendarIcon width={12} height={12} />
              <span>Записать всех</span>
              {!allEnrolled && notEnrolledCount > 0 && (
                <span className="enroll-all-count">{notEnrolledCount}</span>
              )}
            </button>
          </div>
        </div>
        
        {isExpanded && (
          <div className="course-group-content">
            {course.items.map(item => renderGroupItemRow(item, false))}
          </div>
        )}
      </div>
    );
  };

  // Рендер категории с курсами
  const renderCategoryGroup = (category) => {
    const isExpanded = expandedCategories[category.categoryName] || false;
    const color = category.categoryColor;
    
    return (
      <div key={category.categoryName} className="category-group">
        <div 
          onClick={() => toggleCategory(category.categoryName)}
          className="category-header"
          style={{ backgroundColor: color.light, borderBottom: `2px solid ${color.border}` }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleCategory(category.categoryName);
            }
          }}
        >
          <div className="category-header-left">
            <span className="category-icon">{color.icon}</span>
            <ChevronDownIcon isExpanded={isExpanded} />
            <span className="category-name" style={{ color: color.text }}>
              {category.categoryName}
            </span>
            <span className="category-stats">
              ({category.courses.length} курсов, {category.totalEmployees} сотрудников)
            </span>
          </div>
          <div className="category-header-right">
            {category.criticalCount > 0 && (
              <span className="badge-critical-count">{category.criticalCount} крит.</span>
            )}
            {category.urgentCount > 0 && (
              <span className="badge-urgent-count">{category.urgentCount} сроч.</span>
            )}
            {category.planningCount > 0 && (
              <span className="badge-planning-count">{category.planningCount} план.</span>
            )}
            {category.requiredCount > 0 && (
              <span className="badge-required-count">{category.requiredCount} треб.</span>
            )}
          </div>
        </div>
        
        {isExpanded && (
          <div className="category-content">
            {category.courses.map(course => renderCourseGroup(course))}
          </div>
        )}
      </div>
    );
  };

  // Рендер группы по сотрудникам
  const renderByEmployeeGroup = (group) => {
    const isExpanded = expandedGroups[group.id] || false;
    
    return (
      <div key={group.id} className="accordion-group">
        <div 
          onClick={() => toggleGroup(group.id)}
          className="accordion-header"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleGroup(group.id);
            }
          }}
        >
          <ChevronDownIcon isExpanded={isExpanded} />
          <div className="accordion-header-left">
            <a 
              className="accordion-title employee-link-accordion" 
              href={`/settings/employees/${group.employeeId}/trainings`}
              onClick={(e) => e.stopPropagation()}
            >
              {group.employeeName}
            </a>
            <div className="accordion-subtitle">{group.department}</div>
            {group.position && <div className="accordion-subtitle">{group.position}</div>}
          </div>
          <div className="accordion-header-right">
            {group.criticalCount > 0 && (
              <span className="badge-critical-count">{group.criticalCount} крит.</span>
            )}
            {group.urgentCount > 0 && (
              <span className="badge-urgent-count">{group.urgentCount} сроч.</span>
            )}
            {group.planningCount > 0 && (
              <span className="badge-planning-count">{group.planningCount} план.</span>
            )}
            {group.requiredCount > 0 && (
              <span className="badge-required-count">{group.requiredCount} треб.</span>
            )}
            <span className="total-count-badge">{group.totalCourses}</span>
          </div>
        </div>
        
        {isExpanded && (
          <div className="accordion-content">
            <div className="employee-courses-list">
              {group.items.map(item => renderGroupItemRow(item, true))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Основной рендер контента
  const renderContent = () => {
    const groupedData = getGroupedItems();
    
    if (groupedData.type === 'list') {
      if (groupedData.items.length === 0) {
        return (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
            <EmptyStateIcon />
            <p className="text-gray-400">
              {attentionItems.length === 0 
                ? 'Нет элементов, требующих внимания' 
                : 'Нет элементов, соответствующих фильтрам'}
            </p>
          </div>
        );
      }
      return <div className="space-y-3">{groupedData.items.map(item => renderListItem(item))}</div>;
    }
    
    if (groupedData.type === 'byCourseWithCategories') {
      if (groupedData.categories.length === 0) {
        return (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
            <p className="text-gray-400">Нет данных для отображения</p>
          </div>
        );
      }
      return <div className="space-y-4">{groupedData.categories.map(category => renderCategoryGroup(category))}</div>;
    }
    
    if (groupedData.type === 'byEmployee') {
      if (groupedData.groups.length === 0) {
        return (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
            <p className="text-gray-400">Нет данных для отображения</p>
          </div>
        );
      }
      return <div className="space-y-3">{groupedData.groups.map(group => renderByEmployeeGroup(group))}</div>;
    }
    
    return null;
  };

  // Форматирование даты для мероприятий
  const formatEventDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getFormatText = (format) => {
    switch (format) {
      case 'online': return 'Онлайн';
      case 'onsite': return 'Офлайн';
      case 'hybrid': return 'Гибрид';
      default: return '—';
    }
  };

  const getFormatClass = (format) => {
    switch (format) {
      case 'online': return 'event-format-online';
      case 'onsite': return 'event-format-offline';
      case 'hybrid': return 'event-format-hybrid';
      default: return '';
    }
  };

  // Рендер блока мероприятий
  const renderEventsBlock = () => {
    const showUpcomingSkeleton = upcomingEventsLoading || loadingState.upcomingEvents;
    const showPendingSkeleton = pendingEventsLoading || loadingState.pendingEvents;
    
    const isUpcomingActive = eventsTab === EVENTS_TAB.UPCOMING;
    const isPendingActive = eventsTab === EVENTS_TAB.PENDING_CONFIRMATION;
    
    return (
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setEventsTab(EVENTS_TAB.UPCOMING)}
              className={`events-tab-btn ${isUpcomingActive ? 'active' : ''}`}
            >
              <CalendarIcon width={14} height={14} />
              <span>Ближайшие мероприятия</span>
              {upcomingEvents.length > 0 && isUpcomingActive && (
                <span className="events-tab-count">{upcomingEvents.length}</span>
              )}
            </button>
            <button
              onClick={() => setEventsTab(EVENTS_TAB.PENDING_CONFIRMATION)}
              className={`events-tab-btn ${isPendingActive ? 'active' : ''}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>Требуют подтверждения</span>
              {pendingEvents.length > 0 && isPendingActive && (
                <span className="events-tab-count pending">{pendingEvents.length}</span>
              )}
            </button>
          </div>
          <a className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors" href="/events">
            Все мероприятия
            <ArrowRightIcon />
          </a>
        </div>
        
        {isUpcomingActive && (
          <>
            {showUpcomingSkeleton ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="flex gap-3">
                          <div className="h-3 bg-gray-200 rounded w-20"></div>
                          <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                    </div>
                    <div className="border-b border-gray-100 mt-3"></div>
                  </div>
                ))}
              </div>
            ) : upcomingEvents.length > 0 ? (
              <div className="upcoming-events-list">
                {upcomingEvents.map((event, index) => (
                  <a 
                    key={event.id} 
                    href={`/training-events/${event.id}`}
                    className="upcoming-event-item group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="event-date-badge">
                        <div className="event-date-day">
                          {formatEventDate(event.start_date)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="event-title-link text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                            {event.title || 'Без названия'}
                          </h3>
                          <span className={`event-format-tag ${getFormatClass(event.format)}`}>
                            {getFormatText(event.format)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPinIcon />
                              {event.location}
                            </span>
                          )}
                          {event.training_center && (
                            <span className="flex items-center gap-1">
                              🏢 {event.training_center}
                            </span>
                          )}
                          {event.course_name && (
                            <span className="text-gray-400">
                              Курс: {event.course_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="event-participants-info">
                        <UsersIcon />
                        <span>{event.participants_count || 0}</span>
                        {event.max_participants > 0 && (
                          <span className="text-gray-400">/{event.max_participants}</span>
                        )}
                      </div>
                    </div>
                    {index < upcomingEvents.length - 1 && (
                      <div className="border-b border-gray-100 mt-3 pt-1"></div>
                    )}
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-400 mb-3">Нет запланированных мероприятий на ближайший месяц</p>
                <a className="btn-create-training" href="/events">Создать мероприятие</a>
              </div>
            )}
          </>
        )}
        
        {isPendingActive && (
          <>
            {showPendingSkeleton ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="flex gap-3">
                          <div className="h-3 bg-gray-200 rounded w-20"></div>
                          <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                    </div>
                    <div className="border-b border-gray-100 mt-3"></div>
                  </div>
                ))}
              </div>
            ) : pendingEvents.length > 0 ? (
              <div className="pending-events-list">
                {pendingEvents.map((event, index) => (
                  <div key={event.id} className="pending-event-item">
                    <div className="flex items-start gap-3">
                      <div className="pending-event-badge">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="event-title-link text-sm font-medium text-gray-900">
                            {event.title || 'Без названия'}
                          </h3>
                          <span className="status-badge status-awaiting">
                            Мероприятие окончено, требуются действия
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <CalendarIcon width={12} height={12} />
                            {formatEventDate(event.start_date)} — {formatEventDate(event.end_date)}
                          </span>
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPinIcon />
                              {event.location}
                            </span>
                          )}
                          {event.course_name && (
                            <span className="text-gray-400">
                              Курс: {event.course_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="pending-event-actions">
                        <button
                          className="pending-confirm-btn"
                          onClick={() => handleOpenConfirmModal(event)}
                          title="Подтвердить мероприятие"
                        >
                          <CheckCircleIcon />
                          Подтвердить
                        </button>
                      </div>
                    </div>
                    {index < pendingEvents.length - 1 && (
                      <div className="border-b border-gray-100 mt-3 pt-1"></div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-400 mb-2">Нет мероприятий, ожидающих подтверждения</p>
                <p className="text-xs text-gray-300">Все мероприятия подтверждены или отклонены</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Проверка авторизации
  if (authLoading) {
    return <LoadingState message="Проверка авторизации..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (userRole === ROLES.USER) {
    return <Navigate to="/my-trainings" replace />;
  }

  if (loading && loadingState.summary && loadingState.statistics) {
    return <MainPageSkeleton />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => loadAllData(true)} />;
  }

  const showLoadingIndicator = Object.values(loadingState).some(v => v === true);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast уведомления */}
      {toast.show && (
        <div className={`main-toast-notification ${toast.type}`}>
          <span className="main-toast-icon">
            {toast.type === 'success' ? (
              <CheckCircleIcon width={18} height={18} />
            ) : toast.type === 'warning' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            ) : toast.type === 'info' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            ) : (
              <XIcon width={18} height={18} />
            )}
          </span>
          <span className="main-toast-message">{toast.message}</span>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 p-8 min-w-0">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Главная</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Обзор допусков и обучений
                  {employeeStatistics.compliancePercentage > 0 && (
                    <span className="ml-2 text-green-600">
                      • Соответствие: {employeeStatistics.compliancePercentage}%
                    </span>
                  )}
                </p>
              </div>
              <button 
                onClick={() => loadAllData(true, true)} 
                className="btn-ghost text-sm flex items-center gap-1"
                title="Обновить данные"
              >
                <RefreshIcon />
                Обновить
                {showLoadingIndicator && (
                  <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin ml-1"></span>
                )}
              </button>
            </div>

            {/* Four Stats Cards */}
            <div className="stats-cards-row">
              <button 
                className={`bg-white rounded-2xl p-5 border-l-4 ${getCardBorderColor('expired')} shadow-sm hover:shadow-md transition-all duration-200 text-left w-full cursor-pointer`}
                onClick={() => setLevelFilter('expired')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg ${getIconColor('expired')}`}>🔴</span>
                  <span className="text-sm font-medium text-gray-500">Просрочено</span>
                  {loadingState.summary && <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>}
                </div>
                <div className={`text-3xl font-bold ${getCounterColor('expired')}`}>
                  {cardStats.expired.total}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  у {summaryData.statistics.uniqueEmployeesWithExpired} сотрудников
                  {cardStats.expired.totalRegistered > 0 && (
                    <span className="ml-1 text-green-600">
                      (записано: {cardStats.expired.totalRegistered})
                    </span>
                  )}
                </div>
              </button>

              <button 
                className={`bg-white rounded-2xl p-5 border-l-4 ${getCardBorderColor('required')} shadow-sm hover:shadow-md transition-all duration-200 text-left w-full cursor-pointer`}
                onClick={() => setLevelFilter('required')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg ${getIconColor('required')}`}>📋</span>
                  <span className="text-sm font-medium text-gray-500">Требуется пройти</span>
                  {loadingState.summary && <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>}
                </div>
                <div className={`text-3xl font-bold ${getCounterColor('required')}`}>
                  {cardStats.required.total}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  у {summaryData.statistics.uniqueEmployeesRequired} сотрудников
                  {cardStats.required.totalRegistered > 0 && (
                    <span className="ml-1 text-green-600">
                      (записано: {cardStats.required.totalRegistered})
                    </span>
                  )}
                </div>
              </button>

              <button 
                className={`bg-white rounded-2xl p-5 border-l-4 ${getCardBorderColor('expiring')} shadow-sm hover:shadow-md transition-all duration-200 text-left w-full cursor-pointer`}
                onClick={() => setLevelFilter('expiring_month')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg ${getIconColor('expiring')}`}>📅</span>
                  <span className="text-sm font-medium text-gray-500">Ближайший месяц</span>
                  {loadingState.summary && <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>}
                </div>
                <div className={`text-3xl font-bold ${getCounterColor('expiring')}`}>
                  {cardStats.expiringInMonth.total}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  у {summaryData.statistics.uniqueEmployeesExpiringMonth} сотрудников
                  {cardStats.expiringInMonth.totalRegistered > 0 && (
                    <span className="ml-1 text-green-600">
                      (записано: {cardStats.expiringInMonth.totalRegistered})
                    </span>
                  )}
                </div>
              </button>

              <button 
                className={`bg-white rounded-2xl p-5 border-l-4 ${getCardBorderColor('planning')} shadow-sm hover:shadow-md transition-all duration-200 text-left w-full cursor-pointer`}
                onClick={() => setLevelFilter('expiring_two_months')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg ${getIconColor('planning')}`}>📅</span>
                  <span className="text-sm font-medium text-gray-500">Через 1-2 месяца</span>
                  {loadingState.summary && <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>}
                </div>
                <div className={`text-3xl font-bold ${getCounterColor('planning')}`}>
                  {cardStats.expiringInTwoMonths.total}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  у {summaryData.statistics.uniqueEmployeesExpiringTwoMonths} сотрудников
                  {cardStats.expiringInTwoMonths.totalRegistered > 0 && (
                    <span className="ml-1 text-green-600">
                      (записано: {cardStats.expiringInTwoMonths.totalRegistered})
                    </span>
                  )}
                </div>
              </button>
            </div>

            {/* Bottom Stats Bar */}
            <div className="stats-grid">
              <div className="stat-card-item">
                <div className="stat-card-value-black">
                  {loadingState.statistics ? (
                    <div className="inline-block w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                  ) : employeeStatistics.total}
                </div>
                <div className="stat-card-label-small">Сотрудников</div>
              </div>
              <div className="stat-card-item">
                <div className="stat-card-value-green">
                  {loadingState.statistics ? (
                    <div className="inline-block w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                  ) : employeeStatistics.compliant}
                </div>
                <div className="stat-card-label-small">Соответствуют</div>
              </div>
              <div className="stat-card-item">
                <div className="stat-card-value-amber">
                  {loadingState.statistics ? (
                    <div className="inline-block w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                  ) : employeeStatistics.nonCompliant}
                </div>
                <div className="stat-card-label-small">Не соответствуют</div>
              </div>
              <div className="stat-card-item">
                <div className="stat-card-value-red">
                  {cardStats.expired.total}
                </div>
                <div className="stat-card-label-small">Просроченных</div>
              </div>
              <div className="stat-card-item">
                <div className="stat-card-value-yellow-dark">
                  {employeeStatistics.total > 0 
                    ? Math.round((employeeStatistics.nonCompliant / employeeStatistics.total) * 100) 
                    : 0}%
                </div>
                <div className="stat-card-label-small">Не пройдено</div>
              </div>
            </div>

            {/* Блок мероприятий */}
            {renderEventsBlock()}

            {/* Требуют внимания */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-3">Требуют внимания</h2>

              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-xs text-gray-500">Группировка:</span>
                <button onClick={() => setGroupBy('byCourse')} className={`grouping-btn ${groupBy === 'byCourse' ? 'active' : ''}`}>По обучению (по блокам)</button>
                <button onClick={() => setGroupBy('byEmployee')} className={`grouping-btn ${groupBy === 'byEmployee' ? 'active' : ''}`}>По сотруднику</button>
              </div>

              <div className="filters-container">
                <select className="filter-select" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} disabled={loadingFilters}>
                  {departmentsList.length === 0 ? (
                    <option value="all">Загрузка подразделений...</option>
                  ) : (
                    departmentsList.map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))
                  )}
                </select>
                <select className="filter-select" value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)} disabled={loadingFilters}>
                  {categoriesList.length === 0 ? (
                    <option value="all">Загрузка блоков...</option>
                  ) : (
                    categoriesList.filter(cat => cat.name !== 'Все блоки').map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))
                  )}
                </select>
                <select className="filter-select" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
                  <option value="">Все уровни</option>
                  <option value="critical">Критическое</option>
                  <option value="urgent">Срочное</option>
                  <option value="planning">Планирование</option>
                  <option value="required">Требуется пройти</option>
                </select>
              </div>

              <div className="space-y-3">
                {renderContent()}
              </div>
            </div>
          </div>
        </main>
        <footer className="text-center py-4 text-xs text-gray-400 border-t border-gray-100">
          Разработано <span className="font-semibold text-blue-600">St3k</span>
        </footer>
      </div>

      {/* Модальные окна */}
      <EnrollToEventModal
        isOpen={enrollModalOpen}
        onClose={() => {
          setEnrollModalOpen(false);
          setSelectedCourseForEnroll(null);
          setSelectedEmployeeForEnroll(null);
          setBulkEnrollData(null);
        }}
        courseId={selectedCourseForEnroll?.id || bulkEnrollData?.courseId}
        courseName={selectedCourseForEnroll?.name || bulkEnrollData?.courseName}
        employee={selectedEmployeeForEnroll}
        employees={bulkEnrollData?.employees}
        onSuccess={handleEnrollSuccess}
      />

      <ConfirmEventModal
        isOpen={confirmModalOpen}
        event={selectedEventForConfirm}
        onClose={handleCloseConfirmModal}
        onSuccess={handleConfirmSuccess}
        showToast={showToast}
      />

      <CompleteTrainingModal
        isOpen={completeModalOpen}
        onClose={handleCloseCompleteModal}
        onConfirm={(date) => handleCompleteTraining(selectedItemForComplete, date)}
        itemName={selectedItemForComplete?.courseName}
        employeeName={selectedItemForComplete?.employeeName}
      />
    </div>
  );
};

export default MainPage;